import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { ForgotPasswordDialog } from '../components/PasswordDialogs';
import { errorMessage } from '../lib/api';

// Compute the best landing page for a user based on their role & allowed products.
const homeForUser = (user) => {
  if (!user) return '/';
  if (user.role === 'admin') return '/admin';
  const allowed = user.allowed_products || ['pti', 'core', 'streamline'];
  if (allowed.includes('streamline')) return '/streamline';
  if (allowed.includes('pti')) return '/pti';
  if (allowed.includes('core')) return '/core';
  return '/';
};

const Login = () => {
  const { login } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = location.state?.from?.pathname;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const u = await login(email, password);
      // Prefer the requested page IF the user has rights to it, else route by role.
      const dest = fromState && (
        u.role === 'admin' ||
        (fromState.startsWith('/streamline') && (u.allowed_products || []).includes('streamline')) ||
        (fromState.startsWith('/pti') && (u.allowed_products || []).includes('pti')) ||
        (fromState.startsWith('/core') && (u.allowed_products || []).includes('core')) ||
        (fromState.startsWith('/admin') && fromState !== '/admin' && fromState !== '/admin/users' && fromState !== '/admin/regions')
      ) ? fromState : homeForUser(u);
      navigate(dest, { replace: true });
    } catch (ex) {
      setErr(errorMessage(ex, 'Login failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white relative overflow-hidden flex items-center justify-center px-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(800px 500px at 20% 20%, rgba(34,211,238,0.14), transparent 60%), radial-gradient(800px 500px at 80% 80%, rgba(168,85,247,0.14), transparent 60%)',
        }}
      />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          {brand.key === 'streamline' ? (
            <img
              src={brand.logoUrl}
              alt={brand.logoAlt}
              className="h-10 w-auto"
            />
          ) : (
            <span
              className="text-[13px] tracking-[0.32em] font-semibold uppercase bg-clip-text text-transparent select-none"
              style={{ backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)' }}
            >
              Pricing Studio
            </span>
          )}
        </Link>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8">
          <h1 className="text-2xl font-semibold text-white">Sign in</h1>
          <p className="mt-1 text-sm text-white/55">Access the {brand.key === 'streamline' ? brand.studioEyebrow : 'Pricing Studio'}.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-2 w-full h-11 px-4 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20"
                placeholder="you@company.com"
              />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Password</span>
              <div className="mt-2 relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full h-11 pl-4 pr-11 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20"
                  placeholder="••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-white/50 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            {err && (
              <div className="flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-400/[0.06] px-3 py-2.5 text-[13px] text-red-300">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                data-testid="forgot-password-link"
                className="text-[12px] text-white/55 hover:text-cyan-300 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} defaultEmail={email} />
    </div>
  );
};

export default Login;
